import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  IconButton,
  Checkbox,
  Grid,
  InputAdornment,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import WebIcon from '@mui/icons-material/Web';
import DescriptionIcon from '@mui/icons-material/Description';
import LinkIcon from '@mui/icons-material/Link';
import StoreIcon from '@mui/icons-material/Store';
import CodeIcon from '@mui/icons-material/Code';
import { useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/apiClient';
import {
  Application,
  ApplicationModule,
  CreateApplicationPayload,
  Dealer,
  UpdateApplicationPayload,
} from '../types';
import { useToast } from '../state/ToastContext';
import { parseErrorMessage } from '../utils/errorUtils';
import { transformApiResponse } from '../utils/apiMiddleware';
import { TablePaginationControl, usePagination } from '../components/TablePaginationControl';
import CustomButton from '../components/CustomButton';
import FormDialog from '../components/FormDialog';
import { applicationSchema, moduleSchema } from '../schemas/application.schema';
import ConfirmationDialog from '../components/ConfirmationDialog';
import { TableSortLabel } from '@mui/material';
import { useSortableTable } from '../hooks/useSortableTable';
import { ExportMenu } from '../components/ExportMenu';
import { usePersistedColumns } from '../hooks/usePersistedColumns';
import { TableColumnSelector, ColumnDefinition } from '../components/TableColumnSelector';

interface ApplicationFormState {
  id?: string;
  name: string;
  code: string;
  description: string;
  baseUrl: string;
  dealerIds: string[];
  logoData?: string | null;
  logoPreview?: string | null;
}

interface ModuleFormState {
  id?: string;
  code: string;
  name: string;
  description: string;
  color: string;
  dealerIds: string[];
}

const MAX_LOGO_BYTES = 200 * 1024;
const MAX_LOGO_KB = Math.floor(MAX_LOGO_BYTES / 1024);

export default function ApplicationsPage() {
  const { showError, showSuccess } = useToast();
  const { t } = useTranslation();
  const [applications, setApplications] = useState<Application[]>([]);
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<ApplicationFormState | null>(null);
  const [deleteAppTarget, setDeleteAppTarget] = useState<Application | null>(null);
  const [modulesDialogOpen, setModulesDialogOpen] = useState(false);
  const [activeApplicationId, setActiveApplicationId] = useState<string | null>(null);
  const [moduleForm, setModuleForm] = useState<ModuleFormState | null>(null);
  const [deleteModuleTarget, setDeleteModuleTarget] = useState<ApplicationModule | null>(null);
  const [moduleMode, setModuleMode] = useState<'create' | 'edit'>('create');
  const [moduleBusy, setModuleBusy] = useState(false);
  const { page, setPage, rowsPerPage, setRowsPerPage, paginateData } = usePagination(10);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const columns: ColumnDefinition<Application>[] = [
    { id: 'name', label: t('applications.table.name'), sortable: true },
    { id: 'code', label: t('applications.table.code'), sortable: true },
    { id: 'baseUrl', label: t('applications.table.baseUrl'), sortable: true },
    { id: 'dealers', label: t('applications.table.dealers'), sortable: false },
    { id: 'modules', label: t('applications.table.modules'), sortable: false },
    {
      id: 'updatedAt',
      label: t('applications.table.updatedAt'),
      sortable: true,
    },
    { id: 'actions', label: t('applications.table.actions'), sortable: false },
  ];

  const { orderBy, order, handleRequestSort, sortedData } = useSortableTable<Application>(
    applications,
    'name',
    'asc',
  );

  const [visibleColumns, setVisibleColumns] = usePersistedColumns(
    'data-grid-columns-applications',
    ['name', 'code', 'baseUrl', 'dealers', 'modules', 'updatedAt', 'actions'],
  );

  const dealerOptions = useMemo(
    () => [...dealers].sort((a, b) => a.name.localeCompare(b.name)),
    [dealers],
  );
  const activeApplication = useMemo(
    () => applications.find((app) => app.id === activeApplicationId) ?? null,
    [applications, activeApplicationId],
  );
  const activeModuleDealerOptions = useMemo(
    () => [...(activeApplication?.dealers ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [activeApplication],
  );

  const loadDealers = async () => {
    try {
      const data = await api.listDealers(null);
      setDealers(data);
    } catch (err) {
      console.error('Failed to load dealers', err);
    }
  };

  const loadApplications = async (options?: { silent?: boolean }) => {
    if (!options?.silent) {
      setLoading(true);
    }
    try {
      const data = await api.listApplications(null, { includeModules: true });
      setApplications(data);
    } catch (err) {
      const friendlyError = transformApiResponse(err, t);
      showError(friendlyError.message); // Should ideally show title + message + suggestion
      // console.error(friendlyError.technical_reference);
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadDealers();
  }, [null]);

  useEffect(() => {
    loadApplications();
  }, [null]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form) {
      return;
    }

    const name = form.name.trim();
    const code = form.code.trim();
    const dealerIds = form.dealerIds;
    if (!name) {
      showError(t('applications.errors.missingName'));
      return;
    }

    setBusy(true);
    try {
      let application: Application;
      const isEdit = Boolean(form.id);

      if (form.id) {
        // Update existing application
        const updatePayload: UpdateApplicationPayload = {
          name,
          description: form.description.trim() ? form.description.trim() : undefined,
          baseUrl: form.baseUrl.trim() ? form.baseUrl.trim() : undefined,
          ...(typeof form.logoData !== 'undefined' ? { logoData: form.logoData } : {}),
        };
        application = await api.updateApplication(null, form.id, updatePayload);
        // Update dealers separately for edits
        await api.setApplicationDealers(null, application.id, { dealerIds });
      } else {
        // Create new application with dealers in single call
        const createPayload: CreateApplicationPayload = {
          name,
          code: code || undefined,
          description: form.description.trim() ? form.description.trim() : undefined,
          baseUrl: form.baseUrl.trim() ? form.baseUrl.trim() : undefined,
          ...(typeof form.logoData !== 'undefined' ? { logoData: form.logoData } : {}),
          dealerIds,
        };
        application = await api.createApplication(null, createPayload);
      }

      await loadApplications();
      setForm(null);
      showSuccess(
        isEdit
          ? t('applications.messages.updated', 'Aplicação atualizada com sucesso!')
          : t('applications.messages.created', 'Aplicação criada com sucesso!'),
      );
    } catch (err) {
      const friendlyError = transformApiResponse(err, t);
      // We can improve Toast to accept title/suggestion, but for now just message or composite
      showError(`${friendlyError.title}: ${friendlyError.message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmAppDelete = async () => {
    if (!deleteAppTarget) return;
    setBusy(true);
    try {
      await api.deleteApplication(null, deleteAppTarget.id);
      showSuccess(t('applications.messages.deleted', 'Aplicação excluída com sucesso.'));
      await loadApplications();
    } catch (err) {
      const friendlyError = transformApiResponse(err, t);
      showError(friendlyError.message);
    } finally {
      setBusy(false);
      setDeleteAppTarget(null);
    }
  };

  const openModulesDialog = (applicationId: string) => {
    setModuleForm(null);
    setModuleMode('create');
    setActiveApplicationId(applicationId);
    setModulesDialogOpen(true);
  };

  const closeModulesDialog = () => {
    setModulesDialogOpen(false);
    setActiveApplicationId(null);
    setModuleForm(null);
    setModuleMode('create');
  };

  const startCreateModule = () => {
    setModuleMode('create');
    setModuleForm({
      code: '',
      name: '',
      description: '',
      color: '',
      dealerIds: [],
    });
  };

  const startEditModule = (module: ApplicationModule) => {
    setModuleMode('edit');
    setModuleForm({
      id: module.id,
      code: module.code,
      name: module.name,
      description: module.description ?? '',
      color: module.color ?? '',
      dealerIds: module.dealers?.map((dealer) => dealer.id) ?? [],
    });
  };

  const handleModuleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!activeApplication || !moduleForm) {
      return;
    }
    const code = moduleForm.code.trim();
    const name = moduleForm.name.trim();
    const description = moduleForm.description.trim();
    if (!code || !name) {
      showError(t('applications.modules.errors.missingRequired'));
      return;
    }
    setModuleBusy(true);
    try {
      if (moduleMode === 'create') {
        await api.createApplicationModule(null, activeApplication.id, {
          code,
          name,
          description: description || undefined,
          color: moduleForm.color || undefined,
          dealerIds: moduleForm.dealerIds,
        });
      } else if (moduleForm.id) {
        await api.updateApplicationModule(null, activeApplication.id, moduleForm.id, {
          code,
          name,
          description: description || undefined,
          color: moduleForm.color || undefined,
          dealerIds: moduleForm.dealerIds,
        });
      }
      await loadApplications({ silent: true });
      setModuleForm(null);
      setModuleMode('create');
    } catch (err) {
      showError(parseErrorMessage(err, t('applications.modules.errors.save')));
    } finally {
      setModuleBusy(false);
    }
  };

  const handleConfirmModuleDelete = async () => {
    if (!activeApplication || !deleteModuleTarget) return;
    setModuleBusy(true);
    try {
      await api.deleteApplicationModule(null, activeApplication.id, deleteModuleTarget.id);
      showSuccess(t('applications.modules.messages.deleted', 'Módulo excluído com sucesso.'));
      await loadApplications({ silent: true });
    } catch (err) {
      showError(parseErrorMessage(err, t('applications.modules.errors.delete')));
    } finally {
      setModuleBusy(false);
      setDeleteModuleTarget(null);
    }
  };

  return (
    <Stack spacing={3}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {t('applications.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('applications.subtitle')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <ExportMenu
            data={sortedData}
            filename="applications-export"
            columns={[
              { id: 'name', label: t('applications.table.name') },
              { id: 'code', label: t('applications.table.code') },
              { id: 'baseUrl', label: t('applications.table.baseUrl') },
              {
                id: 'modules',
                label: 'Modules Count',
                transform: (_, row) => (row.modules?.length || 0).toString(),
              },
              {
                id: 'updatedAt',
                label: t('applications.table.updatedAt'),
                transform: (val) => (val ? new Date(val).toLocaleDateString() : '—'),
              },
            ]}
          />
          <CustomButton
            variant="primary"
            onClick={() =>
              setForm({
                name: '',
                code: '',
                description: '',
                baseUrl: '',
                dealerIds: [],
                logoData: undefined,
                logoPreview: null,
              })
            }
          >
            {t('applications.new')}
          </CustomButton>
          <Tooltip title={t('common.refresh')}>
            <span>
              <CustomButton
                variant="icon"
                icon={<RefreshIcon />}
                iconPosition="only"
                customSize={{ width: 36, height: 36 }}
                onClick={() => loadApplications()}
                disabled={loading}
              />
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <Card>
        <CardContent>
          {loading ? (
            <Typography variant="body2" color="text.secondary">
              {t('applications.loading')}
            </Typography>
          ) : applications.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('applications.empty')}
            </Typography>
          ) : (
            <>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
                <TableColumnSelector
                  columns={columns}
                  visibleColumns={visibleColumns}
                  onChange={(cols) => setVisibleColumns(cols as string[])}
                />
              </Stack>
              <TableContainer component={Box} sx={{ overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {visibleColumns.includes('name') && (
                        <TableCell>
                          <TableSortLabel
                            active={orderBy === 'name'}
                            direction={orderBy === 'name' ? order : 'asc'}
                            onClick={() => handleRequestSort('name')}
                          >
                            {t('applications.table.name')}
                          </TableSortLabel>
                        </TableCell>
                      )}
                      {visibleColumns.includes('code') && (
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          <TableSortLabel
                            active={orderBy === 'code'}
                            direction={orderBy === 'code' ? order : 'asc'}
                            onClick={() => handleRequestSort('code')}
                          >
                            {t('applications.table.code')}
                          </TableSortLabel>
                        </TableCell>
                      )}
                      {visibleColumns.includes('baseUrl') && (
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                          <TableSortLabel
                            active={orderBy === 'baseUrl'}
                            direction={orderBy === 'baseUrl' ? order : 'asc'}
                            onClick={() => handleRequestSort('baseUrl')}
                          >
                            {t('applications.table.baseUrl')}
                          </TableSortLabel>
                        </TableCell>
                      )}
                      {visibleColumns.includes('dealers') && (
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                          {t('applications.table.dealers')}
                        </TableCell>
                      )}
                      {visibleColumns.includes('modules') && (
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          {t('applications.table.modules')}
                        </TableCell>
                      )}
                      {visibleColumns.includes('updatedAt') && (
                        <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' } }}>
                          <TableSortLabel
                            active={orderBy === 'updatedAt'}
                            direction={orderBy === 'updatedAt' ? order : 'asc'}
                            onClick={() => handleRequestSort('updatedAt')}
                          >
                            {t('applications.table.updatedAt')}
                          </TableSortLabel>
                        </TableCell>
                      )}
                      {visibleColumns.includes('actions') && (
                        <TableCell align="right">{t('applications.table.actions')}</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginateData(sortedData).map((application) => (
                      <TableRow key={application.id}>
                        {visibleColumns.includes('name') && (
                          <TableCell>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              {application.logoUrl && (
                                <Avatar
                                  src={application.logoUrl}
                                  alt={application.name}
                                  variant="rounded"
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    bgcolor: 'grey.100',
                                  }}
                                />
                              )}
                              <Stack spacing={0.5}>
                                <Typography fontWeight={600}>{application.name}</Typography>
                                {application.description && (
                                  <Typography variant="body2" color="text.secondary">
                                    {application.description}
                                  </Typography>
                                )}
                              </Stack>
                            </Stack>
                          </TableCell>
                        )}
                        {visibleColumns.includes('code') && (
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                            <Typography component="span" fontFamily="monospace">
                              {application.code}
                            </Typography>
                          </TableCell>
                        )}
                        {visibleColumns.includes('baseUrl') && (
                          <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                            {application.baseUrl ?? '—'}
                          </TableCell>
                        )}
                        {visibleColumns.includes('dealers') && (
                          <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                            {application.dealers && application.dealers.length > 0 ? (
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {application.dealers.map((dealer) => (
                                  <Chip
                                    key={dealer.id}
                                    label={dealer.name}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                ))}
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                {t('applications.noDealers')}
                              </Typography>
                            )}
                          </TableCell>
                        )}
                        {visibleColumns.includes('modules') && (
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                            {application.modules && application.modules.length > 0 ? (
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {application.modules.slice(0, 2).map((module) => (
                                  <Chip
                                    key={module.id}
                                    label={module.name}
                                    size="small"
                                    variant={module.color ? 'filled' : 'outlined'}
                                    sx={
                                      module.color
                                        ? {
                                            bgcolor: module.color,
                                            color: 'white',
                                            fontWeight: 500,
                                            '& .MuiChip-label': { px: 1 },
                                          }
                                        : undefined
                                    }
                                  />
                                ))}
                                {application.modules.length > 5 && (
                                  <Chip
                                    label={`+${application.modules.length - 5}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontWeight: 600 }}
                                  />
                                )}
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                {t('applications.noModules')}
                              </Typography>
                            )}
                          </TableCell>
                        )}
                        {visibleColumns.includes('updatedAt') && (
                          <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' } }}>
                            {new Date(application.updatedAt).toLocaleString()}
                          </TableCell>
                        )}
                        {visibleColumns.includes('actions') && (
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Tooltip title={t('applications.actions.manageModules')}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => openModulesDialog(application.id)}
                                >
                                  <ViewModuleIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('applications.actions.edit')}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() =>
                                    setForm({
                                      id: application.id,
                                      name: application.name,
                                      code: application.code,
                                      description: application.description ?? '',
                                      baseUrl: application.baseUrl ?? '',
                                      dealerIds:
                                        application.dealers?.map((dealer) => dealer.id) ?? [],
                                      logoData: undefined,
                                      logoPreview: application.logoUrl ?? null,
                                    })
                                  }
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('applications.actions.delete')}>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => setDeleteAppTarget(application)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePaginationControl
                count={sortedData.length}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={setPage}
                onRowsPerPageChange={setRowsPerPage}
              />
            </>
          )}
        </CardContent>
      </Card>

      <FormDialog
        open={Boolean(form)}
        onClose={() => setForm(null)}
        title={form?.id ? t('applications.dialogEditTitle') : t('applications.dialogTitle')}
        subtitle={form?.id ? t('applications.dialogEditSubtitle', 'Editar detalhes da aplicação') : t('applications.dialogSubtitle', 'Nova Aplicação')}
        icon={form?.logoPreview ? (
          <Box
            component="img"
            src={form.logoPreview}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
          />
        ) : form?.id ? (
          <EditIcon />
        ) : (
          <WebIcon />
        )}
        maxWidth="md"
        loading={busy}
        disableBackdropClick
        formId="application-form"
        submitText={form?.id ? t('applications.dialogSubmitEdit') : t('applications.dialogSubmit')}
        disableSubmit={!applicationSchema.safeParse(form).success}
      >
        <Stack
          spacing={3}
          component="form"
          id="application-form"
          onSubmit={handleSubmit}
          sx={{ py: 2 }}
        >
          <Grid container spacing={2}>
            {/* Logo Column */}
            <Grid
              item
              xs={12}
              sm={3}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  aspectRatio: '1/1',
                  borderRadius: 2,
                  border: '1px dashed rgba(0,0,0,0.2)',
                  display: 'flex',
                  textAlign: 'center',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  bgcolor: 'background.default',
                  position: 'relative',
                }}
              >
                {form?.logoPreview ? (
                  <Box
                    component="img"
                    src={form.logoPreview}
                    alt="Logo"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      p: 1,
                    }}
                  />
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    {t('applications.form.logoEmpty', 'Sem Logo')}
                  </Typography>
                )}
              </Box>
              <label style={{ width: '100%' }}>
                <CustomButton variant="outline" disabled={busy} fullWidth onClick={() => {}}>
                  {t('common.upload', 'Upload')}
                </CustomButton>
                <input
                  hidden
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  onChange={async (event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (!file || !form) return;
                    if (file.size > MAX_LOGO_BYTES) {
                      showError(
                        t('applications.errors.logoTooLarge', {
                          maxKb: MAX_LOGO_KB,
                        }),
                      );
                      return;
                    }
                    try {
                      const reader = new FileReader();
                      const dataUrl = await new Promise<string>((resolve, reject) => {
                        reader.onload = () => {
                          if (typeof reader.result === 'string') resolve(reader.result);
                          else reject(new Error('Invalid logo data.'));
                        };
                        reader.onerror = () => reject(new Error('Failed to read logo.'));
                        reader.readAsDataURL(file);
                      });
                      setForm((prev) =>
                        prev
                          ? {
                              ...prev,
                              logoData: dataUrl,
                              logoPreview: dataUrl,
                            }
                          : prev,
                      );
                    } catch (readError) {
                      console.error(readError);
                      showError(t('applications.errors.logoRead'));
                    }
                  }}
                />
              </label>
              {form?.logoPreview && (
                <CustomButton
                  variant="secondary"
                  fullWidth
                  onClick={() =>
                    setForm((f) => (f ? { ...f, logoData: null, logoPreview: null } : f))
                  }
                  disabled={busy}
                >
                  {t('common.remove', 'Remover')}
                </CustomButton>
              )}
            </Grid>

            {/* Main Fields Column */}
            <Grid item xs={12} sm={9}>
              <Grid container spacing={2}>
                {/* Dealers (Row 1) */}
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="applications-dealers-select-label">
                      {t('applications.form.dealers')}
                    </InputLabel>
                    <Select
                      labelId="applications-dealers-select-label"
                      label={t('applications.form.dealers')}
                      multiple
                      value={form?.dealerIds ?? []}
                      onChange={(event) =>
                        setForm((prev) =>
                          prev
                            ? {
                                ...prev,
                                dealerIds:
                                  typeof event.target.value === 'string'
                                    ? event.target.value.split(',')
                                    : (event.target.value as string[]),
                              }
                            : prev,
                        )
                      }
                      renderValue={(selected) =>
                        selected
                          .map(
                            (id) => dealerOptions.find((dealer) => dealer.id === id)?.name ?? id,
                          )
                          .join(', ')
                      }
                      disabled={busy}
                      startAdornment={
                        <InputAdornment position="start">
                          <StoreIcon
                            fontSize="small"
                            sx={{ color: 'text.secondary', ml: 1, mr: -0.5 }}
                          />
                        </InputAdornment>
                      }
                    >
                      {dealerOptions.length === 0 && (
                        <MenuItem value="" disabled>
                          {t('applications.form.noDealersAvailable')}
                        </MenuItem>
                      )}
                      {dealerOptions.map((dealer) => (
                        <MenuItem key={dealer.id} value={dealer.id}>
                          <Checkbox checked={form?.dealerIds.includes(dealer.id) ?? false} />
                          <ListItemText primary={dealer.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Name (Row 2) */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('applications.form.name')}
                    value={form?.name ?? ''}
                    onChange={(event) =>
                      setForm((prev) => (prev ? { ...prev, name: event.target.value } : prev))
                    }
                    required
                    disabled={busy}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <WebIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Code & Base URL (Row 3) */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('applications.table.code')}
                    value={form?.code ?? ''}
                    onChange={(event) =>
                      setForm((prev) => (prev ? { ...prev, code: event.target.value } : prev))
                    }
                    disabled={busy || Boolean(form?.id)}
                    placeholder={t('applications.form.codePlaceholder', 'Ex: MY_APP')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CodeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('applications.form.baseUrl')}
                    type="url"
                    value={form?.baseUrl ?? ''}
                    onChange={(event) =>
                      setForm((prev) => (prev ? { ...prev, baseUrl: event.target.value } : prev))
                    }
                    disabled={busy}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LinkIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Description (Row 4) */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('applications.form.description')}
                    value={form?.description ?? ''}
                    multiline
                    minRows={2}
                    onChange={(event) =>
                      setForm((prev) =>
                        prev ? { ...prev, description: event.target.value } : prev,
                      )
                    }
                    disabled={busy}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment
                          position="start"
                          sx={{ alignSelf: 'flex-start', mt: 1.5 }}
                        >
                          <DescriptionIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Stack>
      </FormDialog>

      <ConfirmationDialog
        open={Boolean(deleteAppTarget)}
        title={t('common.confirmDelete')}
        content={t('applications.confirmDelete', {
          name: deleteAppTarget?.name,
        })}
        onClose={() => setDeleteAppTarget(null)}
        onConfirm={handleConfirmAppDelete}
        severity="error"
        confirmText={t('common.delete')}
        loading={busy}
      />

      <Dialog
        open={modulesDialogOpen && Boolean(activeApplication)}
        onClose={() => {
          if (moduleBusy) return;
          closeModulesDialog();
        }}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>
          {t('applications.modules.dialogTitle', {
            name: activeApplication?.name ?? '',
          })}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} sx={{ py: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" fontWeight={600}>
                {t('applications.modules.listTitle')}
              </Typography>
              <CustomButton
                variant="secondary"
                onClick={startCreateModule}
                disabled={moduleBusy || !activeApplication}
              >
                {t('applications.modules.new')}
              </CustomButton>
            </Stack>
            {activeApplication &&
            activeApplication.modules &&
            activeApplication.modules.length > 0 ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('applications.modules.table.code')}</TableCell>
                    <TableCell>{t('applications.modules.table.name')}</TableCell>
                    <TableCell>{t('applications.modules.table.description')}</TableCell>
                    <TableCell>{t('applications.modules.table.color')}</TableCell>
                    <TableCell>{t('applications.modules.table.dealers')}</TableCell>
                    <TableCell align="right">{t('applications.modules.table.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeApplication.modules?.map((module) => (
                    <TableRow key={module.id}>
                      <TableCell>
                        <Typography component="span" fontFamily="monospace">
                          {module.code}
                        </Typography>
                      </TableCell>
                      <TableCell>{module.name}</TableCell>
                      <TableCell>
                        {module.description ? (
                          <Typography variant="body2" color="text.secondary">
                            {module.description}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {t('applications.modules.noDescription')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {module.color ? (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: '50%',
                                bgcolor: module.color,
                                border: '1px solid rgba(0,0,0,0.1)',
                              }}
                            />
                            <Typography variant="body2" fontFamily="monospace">
                              {module.color}
                            </Typography>
                          </Box>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>
                        {module.dealers && module.dealers.length > 0 ? (
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {module.dealers.map((dealer) => (
                              <Chip
                                key={dealer.id}
                                label={dealer.name}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {t('applications.modules.noDealers')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title={t('applications.modules.actions.edit')}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => startEditModule(module)}
                              disabled={moduleBusy}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('applications.modules.actions.delete')}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteModuleTarget(module)}
                              disabled={moduleBusy}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('applications.modules.empty')}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <CustomButton variant="secondary" onClick={closeModulesDialog} disabled={moduleBusy}>
            {t('common.cancel')}
          </CustomButton>
        </DialogActions>
      </Dialog>

      <FormDialog
        open={Boolean(moduleForm) && modulesDialogOpen}
        onClose={() => {
          setModuleForm(null);
          setModuleMode('create');
        }}
        title={moduleMode === 'create'
            ? t('applications.modules.dialogCreateTitle')
            : t('applications.modules.dialogEditTitle')}
        maxWidth="sm"
        loading={moduleBusy}
        disableBackdropClick
        formId="module-form"
        submitText={moduleMode === 'create'
              ? t('applications.modules.dialogSubmitCreate')
              : t('applications.modules.dialogSubmitEdit')}
        disableSubmit={!moduleSchema.safeParse(moduleForm).success}
      >
        <Stack
          spacing={2}
          component="form"
          id="module-form"
          onSubmit={handleModuleSubmit}
          sx={{ pt: 1 }}
        >
          <TextField
            label={t('applications.modules.form.code')}
            value={moduleForm?.code ?? ''}
            onChange={(event) =>
              setModuleForm((prev) => (prev ? { ...prev, code: event.target.value } : prev))
            }
            required
            disabled={moduleBusy || !modulesDialogOpen}
          />
          <TextField
            label={t('applications.modules.form.name')}
            value={moduleForm?.name ?? ''}
            onChange={(event) =>
              setModuleForm((prev) => (prev ? { ...prev, name: event.target.value } : prev))
            }
            required
            disabled={moduleBusy || !modulesDialogOpen}
          />
          <TextField
            label={t('applications.modules.form.description')}
            value={moduleForm?.description ?? ''}
            onChange={(event) =>
              setModuleForm((prev) =>
                prev ? { ...prev, description: event.target.value } : prev,
              )
            }
            multiline
            minRows={2}
            disabled={moduleBusy || !modulesDialogOpen}
          />
          <Stack spacing={1}>
            <TextField
              label={t('applications.modules.form.color')}
              value={moduleForm?.color ?? ''}
              onChange={(event) =>
                setModuleForm((prev) => (prev ? { ...prev, color: event.target.value } : prev))
              }
              placeholder="#000000"
              disabled={moduleBusy || !modulesDialogOpen}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box
                      component="label"
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: moduleForm?.color || '#000000',
                        border: '1px solid rgba(0,0,0,0.1)',
                        cursor: 'pointer',
                        display: 'inline-block',
                        overflow: 'hidden',
                        boxShadow: 1,
                        '&:hover': { opacity: 0.8 },
                      }}
                    >
                      <input
                        type="color"
                        value={
                          moduleForm?.color && /^#[0-9A-F]{6}$/i.test(moduleForm.color)
                            ? moduleForm.color
                            : '#000000'
                        }
                        onChange={(e) =>
                          setModuleForm((prev) =>
                            prev ? { ...prev, color: e.target.value } : prev,
                          )
                        }
                        style={{
                          opacity: 0,
                          width: '100%',
                          height: '100%',
                          cursor: 'pointer',
                          padding: 0,
                          border: 'none',
                        }}
                        disabled={moduleBusy || !modulesDialogOpen}
                      />
                    </Box>
                  </InputAdornment>
                ),
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              * Clique no círculo para abrir o seletor de cores
            </Typography>
          </Stack>
          <FormControl fullWidth>
            <InputLabel id="applications-modules-dealers-select-label">
              {t('applications.modules.form.dealers')}
            </InputLabel>
            <Select
              labelId="applications-modules-dealers-select-label"
              label={t('applications.modules.form.dealers')}
              multiple
              value={moduleForm?.dealerIds ?? []}
              onChange={(event) =>
                setModuleForm((prev) =>
                  prev
                    ? {
                        ...prev,
                        dealerIds:
                          typeof event.target.value === 'string'
                            ? event.target.value.split(',')
                            : (event.target.value as string[]),
                      }
                    : prev,
                )
              }
              renderValue={(selected) =>
                selected
                  .map(
                    (id) =>
                      activeModuleDealerOptions.find((dealer) => dealer.id === id)?.name ?? id,
                  )
                  .join(', ')
              }
              disabled={
                moduleBusy || !modulesDialogOpen || activeModuleDealerOptions.length === 0
              }
            >
              {activeModuleDealerOptions.length === 0 && (
                <MenuItem value="" disabled>
                  {t('applications.modules.form.noDealersAvailable')}
                </MenuItem>
              )}
              {activeModuleDealerOptions.map((dealer) => (
                <MenuItem key={dealer.id} value={dealer.id}>
                  <Checkbox checked={moduleForm?.dealerIds.includes(dealer.id) ?? false} />
                  <ListItemText primary={dealer.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </FormDialog>

      <ConfirmationDialog
        open={Boolean(deleteModuleTarget)}
        title={t('common.confirmDelete')}
        content={t('applications.modules.confirmDelete', {
          name: deleteModuleTarget?.name,
        })}
        onClose={() => setDeleteModuleTarget(null)}
        onConfirm={handleConfirmModuleDelete}
        severity="error"
        confirmText={t('common.delete')}
        loading={moduleBusy}
      />
    </Stack>
  );
}
